import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Alert, Button, Container, Typography } from '@mui/material';
import { Home as HomeIcon } from '@mui/icons-material';
import PublicPetProfile from './PublicPetProfile';
import { PetShareService } from '../services/petShareService';
import { PetProfile } from '../types/onboarding';

interface PetShareData {
  pet: PetProfile;
  shareLink: any;
}

export const PublicPetProfileRoute: React.FC = () => {
  const { shareLinkId } = useParams<{ shareLinkId: string }>();
  const [petData, setPetData] = useState<PetShareData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPetProfile = async () => {
      if (!shareLinkId) {
        setError('Invalid share link');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Validate share link ID format
        if (!PetShareService.isValidShareLinkId(shareLinkId)) {
          setError('Invalid share link format');
          setLoading(false);
          return;
        }

        // Get pet data from share link
        const result = await PetShareService.getPetFromShareLink(shareLinkId);
        
        if (!result) {
          setError('Pet profile not found or share link has expired');
          setLoading(false);
          return;
        }

        setPetData(result);
      } catch (error) {
        console.error('Error loading pet profile:', error);
        setError('Failed to load pet profile');
      } finally {
        setLoading(false);
      }
    };

    loadPetProfile();
  }, [shareLinkId]);

  const handleBackToSite = () => {
    window.location.href = window.location.origin;
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={64} sx={{ mb: 3 }} />
          <Typography variant="h6" color="text.secondary">
            Loading pet profile...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error || !petData) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || 'Pet profile not found'}
          </Alert>
          <Button
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={handleBackToSite}
          >
            Back to PetSnap
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <PublicPetProfile 
      petId={petData.pet.id!} 
      userId={petData.shareLink.userId} 
    />
  );
};

export default PublicPetProfileRoute;
